import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, linksAPI, dronesAPI, usersAPI, messagesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft,
  Radio,
  Link2,
  Plane,
  Users,
  MessageSquare,
  Plus,
  Trash2,
  Copy,
  Share2,
  Mail,
  Clock,
  ExternalLink,
  Maximize2,
  X,
  AlertTriangle,
  Bell,
  FileText,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import FullscreenPlayer from '../components/FullscreenPlayer';

const EXPIRY_OPTIONS = [
  { value: '24h', label: '24 Horas' },
  { value: '3d', label: '3 Dias' },
  { value: '7d', label: '7 Dias' },
  { value: '30d', label: '30 Dias' },
  { value: '90d', label: '90 Dias' },
  { value: '6m', label: '6 Meses' },
  { value: '1y', label: '1 Ano' },
  { value: 'unlimited', label: 'Tempo Indeterminado' },
];

const CountdownTimer = ({ expiresAt }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining('Sem expiração');
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expirado');
        setIsExpired(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return (
    <div className={`flex items-center gap-2 ${isExpired ? 'text-red-500' : 'text-blue-600'}`}>
      <Clock size={14} />
      <span className="text-xs font-mono countdown">{timeRemaining}</span>
      {isExpired && <span className="badge-danger">EXPIRADO</span>}
    </div>
  );
};

// PlayerCard component (cleaned)

const PlayerCard = ({ player, onCreateLink, onDelete, projectId }) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [expiryOption, setExpiryOption] = useState('7d');
  const [expandedVideo, setExpandedVideo] = useState(false);

  const handleCreateLink = async () => {
    try {
      await onCreateLink({
        iframe_url: player.url,
        project_id: projectId,
        name: linkName || player.name,
        expiry_option: expiryOption
      });
      setShowLinkDialog(false);
      setLinkName('');
      toast.success('Link criado com sucesso!');
    } catch (error) {
      toast.error('Erro ao criar link');
    }
  };

  return (
    <>
      <div className="card-hud overflow-hidden" data-testid={`player-${player.id}`}>
        <div className="aspect-video">
          <iframe
            src={player.url}
            className="w-full h-full"
            frameBorder="0"
            allowFullScreen
            title={player.name}
          />
        </div>
        <div className="px-3 py-2 flex items-center justify-between bg-white border-t border-gray-100">
          <p className="text-navy text-sm font-medium truncate flex-1 min-w-0">{player.name}</p>
          <div className="flex gap-1 flex-shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowLinkDialog(true)}
              className="text-gold hover:text-gold hover:bg-gold/10 h-7 px-2 text-[10px]"
              data-testid={`create-link-${player.id}`}
            >
              <Link2 size={12} className="mr-1" /> GERAR LINK
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setExpandedVideo(true)}
              className="text-gray-300 hover:text-gold hover:bg-gold/10 h-7 w-7"
            >
              <Maximize2 size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(player.id)}
              className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-7 w-7"
              data-testid={`delete-player-${player.id}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Create Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-white border border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gold font-rajdhani uppercase tracking-wider">
              GERAR LINK - {player.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-500 font-mono text-xs uppercase">Nome do Link</Label>
              <Input
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                className="input-hud"
                placeholder={player.name}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500 font-mono text-xs uppercase">Validade</Label>
              <Select value={expiryOption} onValueChange={setExpiryOption}>
                <SelectTrigger className="input-hud">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200">
                  {EXPIRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-navy hover:bg-gold/10">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowLinkDialog(false)} className="btn-secondary flex-1">
                CANCELAR
              </Button>
              <Button onClick={handleCreateLink} className="btn-primary flex-1">
                CRIAR LINK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FullscreenPlayer
        open={expandedVideo}
        onClose={() => setExpandedVideo(false)}
        videoUrl={player.url}
        videoName={player.name}
      />
    </>
  );
};

const SharedLinkCard = ({ link, onDelete }) => {
  const getShareUrl = () => `${window.location.origin}/stream/${link.access_token}`;

  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    toast.success('Link copiado!');
  };

  const shareWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(`Acesse o stream: ${link.name}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`Stream RPAS: ${link.name}`);
    const body = encodeURIComponent(`Acesse o stream através do link:\n\n${getShareUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const isExpired = link.expires_at && new Date(link.expires_at) < new Date();

  return (
    <div className={`card-hud p-4 space-y-3 ${isExpired ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-navy font-medium">{link.name}</h4>
          <p className="text-gray-500 text-xs font-mono mt-1">
            {link.iframe_url.split('stream=')[1]?.split('&')[0] || 'Stream'}
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => onDelete(link.id)}
          className="text-gray-500 hover:text-red-500 hover:bg-alert-red/10 h-8 w-8"
        >
          <Trash2 size={14} />
        </Button>
      </div>

      <CountdownTimer expiresAt={link.expires_at} />

      {!isExpired && (
        <>
          <div className="flex gap-2">
            <Button onClick={copyLink} className="btn-secondary flex-1 text-xs h-8">
              <Copy size={12} className="mr-1" />
              COPIAR
            </Button>
            <Button 
              onClick={() => window.open(getShareUrl(), '_blank')}
              variant="ghost"
              size="icon"
              className="text-blue-600 hover:text-gold hover:bg-gold/10 h-8 w-8"
            >
              <ExternalLink size={14} />
            </Button>
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <Button 
              onClick={shareWhatsApp}
              className="flex-1 bg-green-600/20 border border-green-600/30 text-green-500 hover:bg-green-600/30 text-xs h-8"
            >
              <Share2 size={12} className="mr-1" />
              WhatsApp
            </Button>
            <Button 
              onClick={shareEmail}
              className="flex-1 bg-blue-600/20 border border-blue-600/30 text-blue-700 hover:bg-blue-600/30 text-xs h-8"
            >
              <Mail size={12} className="mr-1" />
              E-mail
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const API_URL = '/api';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [links, setLinks] = useState([]);
  const [drones, setDrones] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('players');

  const fetchProjectData = async () => {
    try {
      const [projectRes, linksRes, dronesRes, usersRes] = await Promise.all([
        projectsAPI.get(projectId),
        linksAPI.list(projectId),
        dronesAPI.list(projectId),
        usersAPI.list()
      ]);
      
      setProject(projectRes.data);
      setLinks(linksRes.data.filter(l => l.project_id === projectId));
      setDrones(dronesRes.data.filter(d => d.project_id === projectId));
      setUsers(usersRes.data.filter(u => u.project_id === projectId));
    } catch (error) {
      toast.error('Erro ao carregar dados do projeto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const handleCreateLink = async (linkData) => {
    await linksAPI.create(linkData);
    fetchProjectData();
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Excluir este link?')) return;
    await linksAPI.delete(linkId);
    toast.success('Link excluído!');
    fetchProjectData();
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Remover este player do projeto?')) return;
    const updatedPlayers = project.players.filter(p => p.id !== playerId);
    await projectsAPI.update(projectId, { players: updatedPlayers });
    toast.success('Player removido!');
    fetchProjectData();
  };

  const handleDeleteExternalLink = async (linkId) => {
    if (!window.confirm('Remover este link externo?')) return;
    const updatedLinks = project.external_links.filter(l => l.id !== linkId);
    await projectsAPI.update(projectId, { external_links: updatedLinks });
    toast.success('Link externo removido!');
    fetchProjectData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="card-hud p-8 text-center">
        <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
        <p className="text-gray-500">Projeto não encontrado</p>
      </div>
    );
  }

  const players = project.players || [];
  const externalLinks = project.external_links || [];

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/projects')}
          className="text-gray-500 hover:text-gold"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-navy uppercase tracking-wider">
            {project.name}
          </h1>
          {project.description && (
            <p className="text-gray-500 text-sm mt-1">{project.description}</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-hud p-4 flex items-center gap-3">
          <Radio size={20} className="text-gold" />
          <div>
            <p className="text-gray-500 text-xs font-mono">PLAYERS</p>
            <p className="text-xl font-bold text-navy">{players.length}</p>
          </div>
        </div>
        <div className="card-hud p-4 flex items-center gap-3">
          <Link2 size={20} className="text-blue-600" />
          <div>
            <p className="text-gray-500 text-xs font-mono">LINKS</p>
            <p className="text-xl font-bold text-navy">{links.length}</p>
          </div>
        </div>
        <div className="card-hud p-4 flex items-center gap-3">
          <Plane size={20} className="text-green-600" />
          <div>
            <p className="text-gray-500 text-xs font-mono">DRONES</p>
            <p className="text-xl font-bold text-navy">{drones.length}</p>
          </div>
        </div>
        <div className="card-hud p-4 flex items-center gap-3">
          <Users size={20} className="text-cyan-600" />
          <div>
            <p className="text-gray-500 text-xs font-mono">USUÁRIOS</p>
            <p className="text-xl font-bold text-navy">{users.length}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 border border-gray-200 p-1 w-full grid grid-cols-4">
          <TabsTrigger 
            value="players" 
            className="data-[state=active]:bg-gold/10 data-[state=active]:text-gold"
          >
            <Radio size={16} className="mr-2" />
            PLAYERS
          </TabsTrigger>
          <TabsTrigger 
            value="links" 
            className="data-[state=active]:bg-gold/10 data-[state=active]:text-gold"
          >
            <Link2 size={16} className="mr-2" />
            LINKS GERADOS
          </TabsTrigger>
          <TabsTrigger 
            value="external" 
            className="data-[state=active]:bg-gold/10 data-[state=active]:text-gold"
          >
            <ExternalLink size={16} className="mr-2" />
            LINKS EXTERNOS
          </TabsTrigger>
          <TabsTrigger 
            value="docs" 
            className="data-[state=active]:bg-gold/10 data-[state=active]:text-gold"
          >
            <FileText size={16} className="mr-2" />
            DOCUMENTOS
          </TabsTrigger>
        </TabsList>

        {/* Players Tab */}
        <TabsContent value="players" className="mt-6">
          {players.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {players.map((player) => (
                <PlayerCard
                  key={player.id}
                  player={player}
                  projectId={projectId}
                  onCreateLink={handleCreateLink}
                  onDelete={handleDeletePlayer}
                />
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <Radio size={48} className="text-gray-500 mx-auto mb-4" />
              <p className="text-gray-500 font-mono">Nenhum player configurado</p>
              <p className="text-gray-500 text-sm mt-2">Edite o projeto para adicionar players</p>
            </div>
          )}
        </TabsContent>

        {/* Generated Links Tab */}
        <TabsContent value="links" className="mt-6">
          {links.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {links.map((link) => (
                <SharedLinkCard
                  key={link.id}
                  link={link}
                  onDelete={handleDeleteLink}
                />
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <Link2 size={48} className="text-gray-500 mx-auto mb-4" />
              <p className="text-gray-500 font-mono">Nenhum link gerado</p>
              <p className="text-gray-500 text-sm mt-2">Clique em "Gerar Link" em um player</p>
            </div>
          )}
        </TabsContent>

        {/* External Links Tab */}
        <TabsContent value="external" className="mt-6">
          {externalLinks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {externalLinks.map((link) => (
                <div key={link.id} className="card-hud p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-navy font-medium">{link.name}</h4>
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 text-xs hover:text-gold truncate block mt-1"
                      >
                        {link.url}
                      </a>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteExternalLink(link.id)}
                      className="text-gray-500 hover:text-red-500 hover:bg-alert-red/10 h-8 w-8"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <ExternalLink size={48} className="text-gray-500 mx-auto mb-4" />
              <p className="text-gray-500 font-mono">Nenhum link externo</p>
              <p className="text-gray-500 text-sm mt-2">Edite o projeto para adicionar links externos</p>
            </div>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="docs" className="mt-6">
          <div className="flex justify-end mb-4">
            <label className="btn-primary cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium">
              <Upload size={16} /> ANEXAR DOCUMENTO
              <input type="file" className="hidden" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  await axios.post(`${API_URL}/projects/${projectId}/upload-document`, formData, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Documento anexado!');
                  fetchProjectData();
                } catch (err) { toast.error('Erro ao anexar documento'); }
                e.target.value = '';
              }} accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png" data-testid="upload-project-detail-doc" />
            </label>
          </div>
          {(project.documents || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.documents.map((doc, idx) => (
                <div key={doc.id || idx} className="card-hud p-4 hover:border-gold/50 transition-colors flex items-center gap-3">
                  <a href={`${doc.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1">
                    <FileText size={24} className="text-gold flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-navy font-medium text-sm truncate">{doc.filename}</p>
                      <p className="text-gray-400 text-xs font-mono">{doc.uploaded_by} - {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </a>
                  <button onClick={async () => {
                    if (!window.confirm('Excluir este documento?')) return;
                    try {
                      await axios.delete(`${API_URL}/projects/${projectId}/documents/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Documento excluido!');
                      fetchProjectData();
                    } catch (err) { toast.error('Erro ao excluir documento'); }
                  }} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-testid={`delete-detail-doc-${doc.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <FileText size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 font-mono">Nenhum documento anexado</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectDetailPage;
